import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { FarmState } from '../domain/model';
import { createDomain } from '../domain/farm';
import {EntityLink} from './EntityLink';

export type Domain = ReturnType<typeof createDomain>;
export type Sow = FarmState['cerdas'][number];
export type Birth = FarmState['partos'][number];
export type ProtocolEvent = ReturnType<Domain['eventosProtocoloCerdaActual']>[number];

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function ScrollTable({ children, label }: { children: ReactNode; label: string }) {
  const container = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const check = () => setOverflow(element.scrollWidth > element.clientWidth + 2);
    const observer = new ResizeObserver(check);
    observer.observe(element);
    const table = element.querySelector('table');
    if (table) observer.observe(table);
    check();
    return () => observer.disconnect();
  }, [children]);
  return <>
    <div ref={container} className="table-scroll" role="region" aria-label={label} tabIndex={overflow ? 0 : undefined}>
      <table>{children}</table>
    </div>
    {overflow && <div className="table-scroll-hint is-visible">Desliza la tabla para ver todas las columnas ↔</div>}
  </>;
}

export function ActionButton({ children, variant = 'ghost', className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'ghost' | 'wheat' | 'danger' }) {
  return <button type="button" className={`btn ${variant} small ${className}`} {...props}>{children}</button>;
}

export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return <div className="ficha-row"><span className="k">{label}</span><span className="v">{children}</span></div>;
}

export function PrintBadge({ record }: { record: { imprimirPendiente?: boolean; motivoImpresion?: string } }) {
  return record.imprimirPendiente ? <span className="status-pill" title={record.motivoImpresion || 'Hay información nueva sin descargar'}>🖨️ Ficha por descargar</span> : null;
}

export function PrintReminder({ record }: { record: { imprimirPendiente?: boolean; motivoImpresion?: string } }) {
  if (!record.imprimirPendiente) return null;
  return <section className="card" style={{ borderColor: '#D9A13E' }}>
    <b>🖨️ Ficha pendiente de descargar</b>
    <div className="muted">{record.motivoImpresion || 'Hay información nueva'} · Descarga la ficha para actualizar la copia física de la finca.</div>
  </section>;
}

export function Protocols({ events, domain, canEdit, openForm, state, navigate, showLitter = false, showStage = true }: {
  events: ProtocolEvent[];
  domain: Domain;
  canEdit: boolean;
  openForm: (kind: string, ...args: unknown[]) => void;
  state?: FarmState;
  navigate?: (tab:string,id?:string)=>void;
  showLitter?: boolean;
  showStage?: boolean;
}) {
  return <ScrollTable label={showLitter ? 'Protocolos de los lechones' : 'Protocolos del animal'}>
    <thead><tr>{showLitter && <th scope="col">Camada</th>}<th scope="col">Día</th><th scope="col">Fecha</th><th scope="col">Actividad</th>{showStage && <th scope="col">Etapa</th>}<th scope="col">Estado</th><th scope="col">Acciones</th></tr></thead>
    <tbody>{events.map(event => <tr key={event.doneKey}>
      {showLitter && <td>{state&&navigate?<EntityLink state={state} navigate={navigate} reference={{type:'camada',id:event.camadaId||''}}><b>{event.camadaId}</b></EntityLink>:<b>{event.camadaId}</b>}</td>}
      <td><span className="day-pill">Día {event.protocolDay}</span></td>
      <td>{domain.fechaEventoVisible(event)}</td>
      <td><b>{domain.eventAction(event)}</b>{event.detail && <div className="muted">{event.detail}</div>}</td>
      {showStage && <td>{event.protocolGroup}<div className="muted">{event.referenceLabel}</div></td>}
      <td><span className={`status-pill ${domain.isDone(event.doneKey) ? 'done' : ''}`}>{domain.isDone(event.doneKey) ? '✓ Terminada' : 'Pendiente'}</span></td>
      <td><ActionButton disabled={!canEdit} onClick={() => openForm('reprogramarEvento', event)}>✏️ Editar</ActionButton></td>
    </tr>)}</tbody>
  </ScrollTable>;
}
